/**
 * Finishes a “login with Google” (and shows an error if Google refused).
 *
 * The API redirects the browser back to `/?oauth_token=…&oauth_scope=…`; this
 * component stores the session in the matching context — community / shop
 * customer / shop owner — and removes the token from the address bar so it
 * never stays in the URL or the browser history.
 *
 * Mounted once inside all the providers (see App.js).
 */
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { authMe, cMe, customerMe } from '../api';
import { useCommunity } from '../contexts/CommunityContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useOwner } from '../contexts/OwnerContext';
import { useLanguage } from '../i18n';

export default function OAuthHandler() {
  const { t } = useLanguage();
  const { setSession: setCommunitySession } = useCommunity();
  const { setSession: setCustomerSession } = useCustomer();
  const { setFromRegistration } = useOwner();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return undefined;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('oauth_token');
    const error = params.get('login_error');
    if (!token && !error) return undefined;
    handled.current = true;

    // Never keep the token in the address bar / history.
    window.history.replaceState({}, document.title,
                                window.location.pathname + window.location.hash);

    if (error) {
      toast.error(error);
      return undefined;
    }

    const scope = params.get('oauth_scope') || 'community';
    const provider = params.get('oauth_provider') === 'google' ? 'Google' : 'OTP';

    (async () => {
      try {
        if (scope === 'customer') {
          const customer = await customerMe(token);
          setCustomerSession({ access_token: token, customer });
        } else if (scope === 'platform') {
          const user = await authMe(token);
          setFromRegistration({ access_token: token, user });
        } else {
          // cMe() sends the token from localStorage (communityApi interceptor).
          localStorage.setItem('ms_community_token', token);
          const user = await cMe();
          setCommunitySession({ access_token: token, user });
        }
        toast.success(`${t('loginDone')} ✔ ${provider}`);
      } catch (err) {
        localStorage.removeItem('ms_community_token');
        toast.error(err?.response?.data?.detail || t('somethingWrong'));
      }
    })();
    return undefined;
  }, [setCommunitySession, setCustomerSession, setFromRegistration, t]);

  return null;
}
